import { Form, Link, useLoaderData, useLocation } from '@remix-run/react';
import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useCallback,
} from 'react';
import { loader as rootLoader } from '~/root';

// Types
type FixedBottomElement = {
  id: string;
  element: ReactNode;
  zIndex?: number;
  className?: string;
  style?: React.CSSProperties;
};

type CookieConsentContextType = {
  cookieConsent: boolean;
  bannerVisible: boolean;
  bannerHeight: number;
  addFixedElement: (element: FixedBottomElement) => void;
  removeFixedElement: (id: string) => void;
  fixedElements: FixedBottomElement[];
};

const CookieConsentContext = createContext<CookieConsentContextType>({
  cookieConsent: false,
  bannerVisible: false,
  bannerHeight: 0,
  addFixedElement: () => {},
  removeFixedElement: () => {},
  fixedElements: [],
});

export const CookieConsentBanner = forwardRef<
  HTMLDivElement,
  { hideOnRoutes?: string[] }
>(({ hideOnRoutes }, ref) => {
  const { cookieConsent } = useLoaderData<typeof rootLoader>();
  const location = useLocation();

  if (cookieConsent) return null;
  if (hideOnRoutes?.some((route) => location.pathname.startsWith(route)))
    return null;

  return (
    <div
      ref={ref}
      className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center gap-2 border-t bg-white/75 p-2 text-xs md:gap-12"
    >
      <p>
        This website uses cookies to improve your web experience. By visiting
        this website, you agree to the use of cookies.{' '}
        <Link to="/cookie-policy" className="text-theme-blue-dark">
          Learn More.
        </Link>
      </p>
      <Form method="post" preventScrollReset={true}>
        <input type="hidden" name="_action" value="cookie-consent" />
        <input type="hidden" name="cookieConsent" value="true" />
        <input type="hidden" name="redirect" value={location.pathname} />

        <button
          type="submit"
          className="whitespace-nowrap border bg-white px-8 py-1"
        >
          Got it!
        </button>
      </Form>
    </div>
  );
});

CookieConsentBanner.displayName = 'CookieConsentBanner';

export const CookieConsentProvider: React.FC<{
  children: ReactNode;
  hideOnRoutes?: string[];
}> = ({ children, hideOnRoutes }) => {
  const { cookieConsent } = useLoaderData<typeof rootLoader>();
  const location = useLocation();
  const [bannerHeight, setBannerHeight] = useState<number>(0);
  const [fixedElements, setFixedElements] = useState<FixedBottomElement[]>([]);
  const bannerRef = useRef<HTMLDivElement>(null);

  const shouldHideBanner =
    cookieConsent ||
    (hideOnRoutes &&
      hideOnRoutes.some((route) => location.pathname.startsWith(route)));

  const bannerVisible = !shouldHideBanner;

  const addFixedElement = useCallback((element: FixedBottomElement) => {
    setFixedElements((prev) => [
      ...prev.filter((el) => el.id !== element.id), // Remove if already exists
      element,
    ]);
  }, []);

  const removeFixedElement = useCallback((id: string) => {
    setFixedElements((prev) => prev.filter((el) => el.id !== id));
  }, []);

  useEffect(() => {
    if (!bannerVisible) {
      setBannerHeight(0);
      return;
    }

    if (bannerRef.current) {
      setBannerHeight(bannerRef.current.getBoundingClientRect().height);
    }

    const observer = new ResizeObserver(() => {
      if (bannerRef.current) {
        const height = bannerRef.current.getBoundingClientRect().height;
        setBannerHeight(height);
      }
    });

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [bannerVisible, location.pathname]);

  const providerValue: CookieConsentContextType = {
    cookieConsent: !!cookieConsent,
    bannerVisible,
    bannerHeight,
    addFixedElement,
    removeFixedElement,
    fixedElements,
  };

  return (
    <CookieConsentContext.Provider value={providerValue}>
      {children}
      <CookieConsentBanner ref={bannerRef} hideOnRoutes={hideOnRoutes} />
      <FixedBottomRenderer />
    </CookieConsentContext.Provider>
  );
};

const FixedBottomRenderer = () => {
  const { fixedElements, bannerHeight } = useCookieConsent();

  // Sort by zIndex to ensure proper stacking (lower zIndex = closer to bottom)
  const sortedElements = [...fixedElements].sort(
    (a, b) => (a.zIndex || 0) - (b.zIndex || 0),
  );

  let cumulativeHeight = bannerHeight;

  return (
    <>
      {sortedElements.map((element) => {
        const bottomOffset = cumulativeHeight;

        return (
          <FixedBottomElementRenderer
            key={element.id}
            element={element}
            bottomOffset={bottomOffset}
            onHeightChange={(height) => {
              // Update cumulative height for next elements
              cumulativeHeight += height;
            }}
          />
        );
      })}
    </>
  );
};

const FixedBottomElementRenderer = ({
  element,
  bottomOffset,
  onHeightChange,
}: {
  element: FixedBottomElement;
  bottomOffset: number;
  onHeightChange: (height: number) => void;
}) => {
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!elementRef.current) return;

    const updateHeight = () => {
      if (elementRef.current) {
        const height = elementRef.current.getBoundingClientRect().height;
        onHeightChange(height);
      }
    };

    // Initial height calculation
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(elementRef.current);

    return () => observer.disconnect();
  }, [onHeightChange]);

  return (
    <div
      ref={elementRef}
      className={`fixed left-0 right-0 ${element.className || ''}`}
      style={{
        bottom: bottomOffset,
        zIndex: element.zIndex || 40,
        transition: 'bottom 0.2s ease',
        ...element.style,
      }}
    >
      {element.element}
    </div>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider',
    );
  }
  return context;
};

export const useFixedBottomElement = (
  id: string,
  element: ReactNode,
  options: {
    show?: boolean;
    zIndex?: number;
    className?: string;
    style?: React.CSSProperties;
  } = {},
) => {
  const { addFixedElement, removeFixedElement } = useCookieConsent();
  const { show = false, zIndex = 40, className = '', style } = options;

  // Use a ref to track the previous element to avoid unnecessary updates
  const elementRef = useRef(element);
  const prevShowRef = useRef(show);

  useEffect(() => {
    elementRef.current = element;
  });

  useEffect(() => {
    if (show && elementRef.current) {
      addFixedElement({
        id,
        element: elementRef.current,
        zIndex,
        className,
        style,
      });
    } else if (!show || prevShowRef.current !== show) {
      removeFixedElement(id);
    }

    prevShowRef.current = show;
  }, [show, id, zIndex, className, addFixedElement, removeFixedElement, style]);

  useEffect(() => {
    return () => removeFixedElement(id);
  }, [id, removeFixedElement]);

  const showElement = useCallback(() => {
    addFixedElement({
      id,
      element: elementRef.current,
      zIndex,
      className,
      style,
    });
  }, [id, zIndex, className, style, addFixedElement]);

  const hideElement = useCallback(() => {
    removeFixedElement(id);
  }, [id, removeFixedElement]);

  return {
    show: showElement,
    hide: hideElement,
  };
};
